import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { attendanceAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, CalendarDays, ChevronLeft, ChevronRight, Users } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', short: 'P', color: 'bg-emerald-100 text-emerald-700', cellColor: 'bg-emerald-200 text-emerald-800', icon: CheckCircle2 },
  { value: 'absent', label: 'Absent', short: 'A', color: 'bg-red-100 text-red-700', cellColor: 'bg-red-200 text-red-800', icon: XCircle },
  { value: 'wfh', label: 'WFH', short: 'WFH', color: 'bg-blue-100 text-blue-700', cellColor: 'bg-blue-200 text-blue-800', icon: Clock },
  { value: 'half_day', label: 'Half Day', short: 'H', color: 'bg-amber-100 text-amber-700', cellColor: 'bg-amber-200 text-amber-800', icon: Clock },
  { value: 'leave', label: 'Leave', short: 'L', color: 'bg-purple-100 text-purple-700', cellColor: 'bg-purple-200 text-purple-800', icon: CalendarDays },
  { value: 'holiday', label: 'Holiday', short: 'HO', color: 'bg-slate-100 text-slate-600', cellColor: 'bg-slate-200 text-slate-700', icon: CalendarDays },
];

const getStatusBadge = (status) => {
  const opt = STATUS_OPTIONS.find(o => o.value === status);
  return opt ? <Badge className={opt.color}>{opt.label}</Badge> : <Badge className="bg-gray-100 text-gray-500">Unmarked</Badge>;
};

const AttendancePage = () => {
  usePageTitle('Attendance');
  const { user, isAdmin } = useAuth();
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [staffList, setStaffList] = useState([]);
  const [summary, setSummary] = useState([]);
  const [myAttendance, setMyAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markDialog, setMarkDialog] = useState(null); // {staff_id, staff_name, date}
  const [markStatus, setMarkStatus] = useState('present');
  const [markNotes, setMarkNotes] = useState('');
  const [selectedStaffFilter, setSelectedStaffFilter] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdmin()) {
        const [summaryRes, staffRes] = await Promise.all([
          attendanceAPI.getSummary(currentMonth),
          userAPI.getAll({ role: 'staff' }).catch(() => ({ data: [] })),
        ]);
        setSummary(summaryRes.data);
        setStaffList(staffRes.data || []);
      } else {
        const res = await attendanceAPI.getMy(currentMonth);
        setMyAttendance(res.data);
      }
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [currentMonth, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const navigateMonth = (dir) => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const getMonthLabel = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    return new Date(y, m, 0).getDate();
  };

  const handleMark = async () => {
    if (!markDialog) return;
    try {
      await attendanceAPI.mark({
        staff_id: markDialog.staff_id,
        date: markDialog.date,
        status: markStatus,
        notes: markNotes,
      });
      toast.success(`${markDialog.staff_name} marked as ${markStatus}`);
      setMarkDialog(null);
      setMarkNotes('');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const quickMark = async (staffId, staffName, date, status) => {
    try {
      await attendanceAPI.mark({ staff_id: staffId, date, status, notes: '' });
      toast.success(`${staffName}: ${status}`);
      loadData();
    } catch { toast.error('Failed'); }
  };

  // Build weekly (Mon-Sun) calendar weeks for the current month
  const buildCalendarWeeks = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1);
    const lastDay = new Date(y, m, 0);
    // ISO weekday: 1=Mon, 7=Sun. JS getDay: 0=Sun..6=Sat
    const startWeekday = (firstDay.getDay() + 6) % 7; // 0=Mon..6=Sun
    const weeks = [];
    let currentWeek = Array(startWeekday).fill(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      currentWeek.push(d);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    if (currentWeek.length) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }
    return weeks;
  };

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  // Staff view - own attendance
  if (!isAdmin()) {
    return (
      <div className="space-y-5" data-testid="attendance-page">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="w-7 h-7" /> My Attendance</h1>
          <p className="text-blue-100 text-sm mt-1">{getMonthLabel()}</p>
        </div>

        {myAttendance && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {[
                { label: 'Present', val: myAttendance.summary.present, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
                { label: 'Absent', val: myAttendance.summary.absent, color: 'text-red-600 bg-red-50 border-red-200' },
                { label: 'WFH', val: myAttendance.summary.wfh || 0, color: 'text-blue-600 bg-blue-50 border-blue-200' },
                { label: 'Half Day', val: myAttendance.summary.half_day, color: 'text-amber-600 bg-amber-50 border-amber-200' },
                { label: 'Leave', val: myAttendance.summary.leave, color: 'text-purple-600 bg-purple-50 border-purple-200' },
                { label: 'Working Days', val: myAttendance.summary.total_working, color: 'text-slate-700 bg-slate-50 border-slate-200' },
              ].map(s => (
                <Card key={s.label} className={`${s.color} border`}>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{s.val}</p>
                    <p className="text-xs">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card>
              <CardHeader><CardTitle className="text-lg">Attendance Records</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(myAttendance.records || []).map(r => (
                    <div key={r.date} className="flex items-center justify-between p-2 border rounded-lg">
                      <span className="text-sm font-medium">{r.date}</span>
                      {getStatusBadge(r.status)}
                      {r.notes && <span className="text-xs text-muted-foreground">{r.notes}</span>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  // Admin view
  const daysInMonth = getDaysInMonth();

  return (
    <div className="space-y-5" data-testid="attendance-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-7 h-7" /> Staff Attendance</h1>
            <p className="text-blue-100 text-sm mt-1">Mark attendance, track presence and leave</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white" onClick={() => navigateMonth(-1)}><ChevronLeft className="w-5 h-5" /></Button>
            <span className="font-semibold text-lg min-w-[160px] text-center">{getMonthLabel()}</span>
            <Button variant="ghost" size="icon" className="text-white" onClick={() => navigateMonth(1)}><ChevronRight className="w-5 h-5" /></Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Staff', val: summary.length, color: 'text-blue-600' },
          { label: 'Avg Present/Day', val: summary.length > 0 ? Math.round(summary.reduce((a, s) => a + s.present, 0) / Math.max(1, daysInMonth)) : 0, color: 'text-emerald-600' },
          { label: 'Total Absences', val: summary.reduce((a, s) => a + s.absent, 0), color: 'text-red-600' },
          { label: 'Total Leaves', val: summary.reduce((a, s) => a + s.leave, 0), color: 'text-purple-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Mark Today */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Today's Attendance - {todayStr}</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {summary.map(s => {
              const todayStatus = s.days[todayStr];
              return (
                <div key={s.staff_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`today-${s.staff_id}`}>
                  <div>
                    <p className="font-medium text-sm">{s.staff_name}</p>
                    <p className="text-xs text-muted-foreground">{s.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {todayStatus && getStatusBadge(todayStatus)}
                    {STATUS_OPTIONS.map(opt => (
                      <Button
                        key={opt.value}
                        variant={todayStatus === opt.value ? 'default' : 'outline'}
                        size="sm"
                        className={`h-7 text-xs ${todayStatus === opt.value ? '' : ''}`}
                        onClick={() => quickMark(s.staff_id, s.staff_name, todayStr, opt.value)}
                        data-testid={`mark-${s.staff_id}-${opt.value}`}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })}
            {summary.length === 0 && <p className="text-center text-muted-foreground py-4">No staff found</p>}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Calendar Grid (Mon-Sun) */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg">Staff Attendance Calendar (Mon-Sun)</CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Filter:</Label>
              <Select value={selectedStaffFilter} onValueChange={setSelectedStaffFilter}>
                <SelectTrigger className="w-56 h-9" data-testid="attendance-staff-filter">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff ({summary.length})</SelectItem>
                  {summary.map(s => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>{s.staff_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            {STATUS_OPTIONS.map(opt => (
              <span key={opt.value} className={`px-2 py-0.5 rounded ${opt.color} font-medium`}>{opt.short} = {opt.label}</span>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-6 overflow-x-auto">
          {summary.length === 0 && <p className="text-center text-muted-foreground py-4">No staff found</p>}
          {summary
            .filter(s => selectedStaffFilter === 'all' || s.staff_id === selectedStaffFilter)
            .map(s => {
            const weeks = buildCalendarWeeks();
            return (
              <div key={s.staff_id} className="border rounded-lg p-3 bg-slate-50/50" data-testid={`calendar-${s.staff_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-sm">{s.staff_name}</p>
                    <p className="text-xs text-muted-foreground">{s.email}</p>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">P: {s.present}</span>
                    <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">A: {s.absent}</span>
                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">WFH: {s.wfh || 0}</span>
                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">H: {s.half_day}</span>
                    <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">L: {s.leave}</span>
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">HO: {s.holiday || 0}</span>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1 text-xs">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                    <div key={d} className="text-center font-semibold text-muted-foreground p-1">{d}</div>
                  ))}
                  {weeks.flatMap((week, wi) =>
                    week.map((day, di) => {
                      if (!day) return <div key={`${wi}-${di}`} className="h-12 bg-transparent" />;
                      const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
                      const status = s.days[dateStr];
                      const opt = STATUS_OPTIONS.find(o => o.value === status);
                      const isToday = dateStr === todayStr;
                      const isWeekend = di === 5 || di === 6;
                      return (
                        <button
                          key={dateStr}
                          onClick={() => { setMarkDialog({ staff_id: s.staff_id, staff_name: s.staff_name, date: dateStr }); setMarkStatus(status || 'present'); setMarkNotes(''); }}
                          className={`h-12 rounded-md border flex flex-col items-center justify-center transition-all hover:ring-2 hover:ring-blue-400 ${opt ? opt.cellColor : isWeekend ? 'bg-slate-100 text-slate-400' : 'bg-white text-slate-600'} ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                          data-testid={`cal-${s.staff_id}-${dateStr}`}
                          title={`${s.staff_name} - ${dateStr}: ${status || 'unmarked'}`}
                        >
                          <span className="text-[10px] font-medium">{day}</span>
                          {opt && <span className="text-[10px] font-bold">{opt.short}</span>}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Mark Dialog */}
      <Dialog open={!!markDialog} onOpenChange={() => setMarkDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="mark-attendance-dialog">
          <DialogHeader>
            <DialogTitle>Mark Attendance</DialogTitle>
            <DialogDescription>{markDialog?.staff_name} - {markDialog?.date}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Status</Label>
              <Select value={markStatus} onValueChange={setMarkStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={markNotes} onChange={e => setMarkNotes(e.target.value)} placeholder="Reason..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkDialog(null)}>Cancel</Button>
            <Button onClick={handleMark} data-testid="save-attendance-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePage;
